import { useContext, useLayoutEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useLiveUpdates } from "@/live-updates/useLiveUpdates";
import type { Booking } from "@/logic/api";
import { SessionContext } from "@/utility/useSession";
import type { BookingLiveUpdateType, CoreBookingView } from "./bookingTabs";
import { getBookingsView } from "./getBookingsView";
import {
  getLiveBookingChanges,
  type LiveBookingChanges,
} from "./getLiveBookingChanges";

export interface BookingViewSnapshot {
  items: ReadonlyArray<Booking>;
}

interface BookingViewPresentation {
  scopeKey: string;
  snapshot: BookingViewSnapshot;
  dataUpdatedAt: number;
  liveChanges: LiveBookingChanges | null;
}

export interface BookingViewScope {
  userUuid: string | null;
  tenantId: string | null;
  carParkId: number | null;
}

export const coreBookingViewKeys = {
  all: (scope: BookingViewScope) => ["bookings", "core", scope] as const,
  view: (scope: BookingViewScope, view: CoreBookingView) =>
    [...coreBookingViewKeys.all(scope), view] as const,
};

export const useCoreBookingView = (
  view: CoreBookingView,
  liveUpdateTypes: ReadonlyArray<BookingLiveUpdateType>,
  liveUpdatesEnabled: boolean,
  animationsEnabled = true,
) => {
  const shouldAnimateLiveChanges = liveUpdatesEnabled && animationsEnabled;
  const { current: session } = useContext(SessionContext);
  const scope: BookingViewScope = {
    userUuid: session?.site?.user?.uuid ?? null,
    tenantId: session?.site?.tenant?.id ?? null,
    carParkId: session?.site?.home?.id ?? null,
  };
  const hasSessionScope =
    session?.context != null &&
    scope.userUuid != null &&
    scope.tenantId != null &&
    scope.carParkId != null;
  const scopeKey = JSON.stringify([
    view,
    scope.userUuid,
    scope.tenantId,
    scope.carParkId,
  ]);
  const queryKey = coreBookingViewKeys.view(scope, view);

  const query = useQuery<BookingViewSnapshot, Error>({
    queryKey,
    queryFn: async () => {
      const snapshot = { items: await getBookingsView(session.context, view) };
      return snapshot;
    },
    enabled: hasSessionScope,
    refetchOnMount: "always",
    gcTime: 0,
  });
  const [presentation, setPresentation] =
    useState<BookingViewPresentation | null>(null);
  const [pendingLiveScopeKey, setPendingLiveScopeKey] = useState<string | null>(
    null,
  );
  const presentedSnapshotRef = useRef<BookingViewSnapshot | null>(null);
  const currentScopeKey = useRef(scopeKey);
  const liveRevision = useRef(0);
  const activePresentation =
    presentation?.scopeKey === scopeKey ? presentation : null;
  // Holds the old list during a live refetch, then reveal its result with the cue.
  // Later routine query updates bypass this stored presentation immediately.
  const shouldUsePresentation =
    activePresentation != null &&
    (pendingLiveScopeKey === scopeKey ||
      activePresentation.dataUpdatedAt >= query.dataUpdatedAt);
  const presentedSnapshot = shouldUsePresentation
    ? activePresentation.snapshot
    : (query.data ?? activePresentation?.snapshot ?? null);

  useLayoutEffect(() => {
    currentScopeKey.current = scopeKey;
    presentedSnapshotRef.current = presentedSnapshot;
  }, [presentedSnapshot, scopeKey]);

  useLiveUpdates(
    {
      key: `bookings-core-view:${view}`,
      shouldRefresh: (event) =>
        liveUpdateTypes.some((type) => type === event.type),
      refresh: async () => {
        if (!shouldAnimateLiveChanges) {
          await query.refetch({ throwOnError: true });
          return;
        }

        const before = presentedSnapshotRef.current ?? query.data;
        setPendingLiveScopeKey(scopeKey);
        if (before != null) {
          setPresentation((current) =>
            current?.scopeKey === scopeKey && current.snapshot === before
              ? current
              : {
                scopeKey,
                snapshot: before,
                dataUpdatedAt: query.dataUpdatedAt,
                liveChanges: null,
              },
          );
        }

        try {
          const result = await query.refetch({ throwOnError: true });
          if (result.data != null && currentScopeKey.current === scopeKey) {
            const changes =
              before == null
                ? null
                : getLiveBookingChanges(before.items, result.data.items);
            const liveChanges =
              changes == null
                ? null
                : { ...changes, revision: ++liveRevision.current };
            presentedSnapshotRef.current = result.data;
            setPresentation({
              scopeKey,
              snapshot: result.data,
              dataUpdatedAt: result.dataUpdatedAt,
              liveChanges,
            });
          }
        } finally {
          setPendingLiveScopeKey((current) =>
            current === scopeKey ? null : current,
          );
        }
      },
    },
    hasSessionScope && liveUpdatesEnabled && liveUpdateTypes.length > 0,
  );

  return {
    query,
    presentedSnapshot,
    liveChanges:
      shouldAnimateLiveChanges && shouldUsePresentation
        ? activePresentation.liveChanges
        : null,
  };
};
